/**
 * We display this message this to block the feed and search page when users have an expired subscription.
 */

import SVTrackedUpgradeButtonContainer from '@apps/www/src/www/containers/SVTrackedUpgradeButtonContainer';
import { SVButtonSIZES, SVButtonUSES } from '@pkgs/shared-client/components/SVButton';
import cn from '@pkgs/shared-client/helpers/cn';
import ExpiredMessageType from '@pkgs/shared/enums/ExpiredMessageType';
import UpgradeButtonSource from '@pkgs/shared/enums/UpgradeButtonSource';
import { ValueOf } from 'next/dist/shared/lib/constants';
import { useMemo } from 'react';

export default function SVSubscriptionExpiredNotice({
	source,
	expiredMessageType,
}: {
	source: ValueOf<typeof UpgradeButtonSource>;
	expiredMessageType: ValueOf<typeof ExpiredMessageType>;
}) {
	const { title, description, buttonText } = useMemo(() => {
		if (expiredMessageType === ExpiredMessageType.PICPAY) {
			return {
				title: <>Seu acesso pelo PicPay expirou&nbsp;🙁</>,
				description: (
					<>
						<p>
							O plano corporativo do PicPay não foi renovado e seu acesso ao Savee foi
							desativado.
						</p>
						<p>
							Mas calma, você pode continuar explorando inspiração nova todos os dias
							com o plano Premium.
						</p>
						<p>Faça o upgrade e mantenha sua criatividade sempre em movimento.</p>
					</>
				),
				buttonText: 'Voltar para o Savee',
			};
		}

		return {
			title: <>Your account expired&nbsp;🙁</>,
			description: (
				<>
					<p>
						You’re part of a global community that believes great design starts with
						great taste.
					</p>
					<p>
						Go Premium again to keep exploring, stay inspired, and support a platform
						built by and for creatives.
					</p>
					<p>No ads, no distractions, just pure visual focus.</p>
				</>
			),
			buttonText: 'Get back in',
		};
	}, [expiredMessageType]);

	return (
		<div
			className={cn(
				'flex flex-col items-center justify-center',
				(source === UpgradeButtonSource.BLOCKED_FEED_USER ||
					source === UpgradeButtonSource.BLOCKED_BOARDS_USERS) &&
					'-lg:pr-0 min-h-[calc(100vh-var(--nav-height)*2) relative mx-[var(--page-margin)] ',
			)}
		>
			<div className="relative flex h-full min-h-[calc(100vh-var(--nav-height)*2)] flex-col items-center justify-center">
				<div className="relative z-10 flex max-w-[420px] flex-col items-center justify-center space-y-5 rounded-[24px] border-2 border-gray-700 p-14 text-center text-gray-400">
					<p className="type-subnav -md:text-wrap mb-1 text-nowrap font-semibold text-gray-50">
						{title}
					</p>
					{description}

					<div>
						<SVTrackedUpgradeButtonContainer
							className="mt-6 bg-gray-200"
							source={source}
							to="/billing/renew/"
							use={SVButtonUSES.PRIMARY}
							size={SVButtonSIZES.SMALL}
						>
							{buttonText}
						</SVTrackedUpgradeButtonContainer>
					</div>
				</div>
			</div>
		</div>
	);
}
